import { useApolloClient } from '@apollo/client';
import logger from '@orn/shared-web/lib/logger';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { PATHS } from '../../paths';

import { ConnectBankStep, UserInfoStep, VerifyPhoneStep } from './steps';

enum WizardStep {
  UserInfo = 'userInformation',
  VerifyPhone = 'verifyPhone',
  ConnectBank = 'connectBank',
}

const OnboardingWizard = () => {
  const navigate = useNavigate();
  const client = useApolloClient();

  const [currentStep, setCurrentStep] = React.useState<WizardStep>(WizardStep.UserInfo);

  const handleComplete = React.useCallback(() => {
    void client
      .resetStore()
      .then(() => {
        void navigate(PATHS.DASHBOARD, { replace: true });
      })
      .catch(() => {
        logger.error('Failed to reset Apollo client store');
      });
  }, [navigate, client]);

  const handleNext = React.useCallback(() => {
    switch (currentStep) {
      case WizardStep.UserInfo:
        setCurrentStep(WizardStep.VerifyPhone);
        break;
      case WizardStep.VerifyPhone:
        setCurrentStep(WizardStep.ConnectBank);
        break;
      case WizardStep.ConnectBank:
        handleComplete();
        break;
      default:
        break;
    }
  }, [currentStep, handleComplete]);

  const handleBack = React.useCallback(() => {
    switch (currentStep) {
      case WizardStep.VerifyPhone:
        setCurrentStep(WizardStep.UserInfo);
        break;
      case WizardStep.ConnectBank:
        setCurrentStep(WizardStep.VerifyPhone);
        break;
      default:
        break;
    }
  }, [currentStep]);

  const renderStepContent = () => {
    switch (currentStep) {
      case WizardStep.UserInfo:
        return <UserInfoStep onNext={handleNext} />;
      case WizardStep.VerifyPhone:
        return <VerifyPhoneStep onNext={handleNext} onBack={handleBack} />;
      case WizardStep.ConnectBank:
        return <ConnectBankStep onNext={handleNext} onBack={handleBack} />;
      default:
        return null;
    }
  };

  return <div className="flex w-full flex-col items-center">{renderStepContent()}</div>;
};

export default OnboardingWizard;
